/**
 * The top of the rating scale. Declared once so the clamp, the stars, the pill's
 * denominator and the sentence a screen reader hears cannot disagree about what a
 * rating is out of.
 */
export const MAX_RATING = 5;

/**
 * Formats a 0 to 5 rating for display: clamped to that range, one decimal place,
 * with a trailing `.0` removed so a whole rating reads as "4" rather than "4.0".
 *
 * Shared because more than one component shows a rating and they must agree on
 * the rounding: the pill on the partner page and the star in the product card.
 *
 * 0 to 5 rather than 1 to 5 because this range belongs to a product's average rating,
 * which is 0 for a product nothing has been said about yet. One review's own rating is
 * a whole number 1 to 5, per `Review.rating`, and never reaches the floor. Clamping at
 * all is defensive: a rating out of range should render as the nearest plausible one
 * rather than as a bar off the end of the scale.
 */
export function formatRating(rating: number): string {
  const clamped = Math.min(MAX_RATING, Math.max(0, rating));
  const formatted = clamped.toFixed(1);
  return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
}

/**
 * The whole rating as one sentence, for the accessible name of whatever draws it.
 *
 * Shared for the same reason as the formatting, and more urgently: this string is never
 * rendered visibly, so two components drifting apart on it is invisible until someone
 * listens to the page. It keeps the unrounded rating, so a 4.5 is not announced as a 5.
 */
export function ratingLabel(rating: number): string {
  return `Rated ${formatRating(rating)} out of ${MAX_RATING}`;
}
